import React from 'react';
import Button from 'components/UI/V3/Button/Button';
import { InlineEditableTitle } from 'components/UI/V3/Forms/InlineEditableTitle';

import { WizardStepper } from './WizardStepper';

import styles from './EmailCampaignWizardHeader.module.scss';

interface EmailCampaignWizardHeaderProps {
    currentStep: number;
    onClose: () => void;
    onContinue: () => void;
    /** Optional — when provided, a "Back" link is rendered to the left of Continue. */
    onBack?: () => void;
    /** Highest step the user has reached so far — controls which steps are clickable in the stepper. */
    maxReachedStep?: number;
    /** When provided, clicking a reached step in the stepper invokes this handler. */
    onStepClick?: (step: number) => void;
    hideContinueButton?: boolean;
    /** Optional — when true, Continue is disabled visually + non-clickable. */
    continueDisabled?: boolean;
    /**
     * Label of the Continue button. Defaults to `'Continue'`. Step 3 overrides
     * to `'Send'` or `'Schedule'` depending on the selected send mode.
     */
    continueLabel?: string;
    /**
     * Optional secondary action — renders a "Save as Draft" button between
     * Back and Continue when provided. Used in Step 3 so the user has an
     * explicit "exit without committing" path while the Send / Schedule
     * confirmation modal handles the primary commit.
     */
    onSaveAsDraft?: () => void;
    /** Disables Save as Draft while a persistence call is in flight. */
    saveAsDraftDisabled?: boolean;
    /** Campaign name rendered in the inline editable title (left slot). */
    campaignName: string;
    /**
     * Fires when the user commits a new name in the inline editable title
     * (Enter or blur with non-empty draft). Parent should update wizard state
     * AND persist to backend — there is no Continue-gated path for the title
     * edit, so commit = save.
     */
    onCommitCampaignName: (next: string) => void;
    /**
     * Optional — formatted error string (e.g. "A campaign with this name
     * already exists.") to paint the title red and auto-open inline edit.
     */
    nameError?: string | null;
}

export const EmailCampaignWizardHeader: React.FC<EmailCampaignWizardHeaderProps> = ({
    currentStep,
    hideContinueButton = false,
    continueDisabled = false,
    continueLabel = 'Continue',
    maxReachedStep,
    onBack,
    onClose,
    onContinue,
    onStepClick,
    onSaveAsDraft,
    saveAsDraftDisabled = false,
    campaignName,
    onCommitCampaignName,
    nameError
}) => {
    return (
        <div className={styles.header}>
            <div className={styles.titleSlot}>
                <InlineEditableTitle
                    value={campaignName}
                    onChange={onCommitCampaignName}
                    error={nameError}
                    placeholder="Untitled Campaign"
                    maxLength={100}
                    ariaLabel="Campaign name"
                />
            </div>

            <WizardStepper
                currentStep={currentStep}
                maxReachedStep={maxReachedStep}
                onStepClick={onStepClick}
            />

            <div className={styles.actions}>
                {onBack && (
                    <button
                        type="button"
                        className={styles.backLink}
                        onClick={onBack}
                    >
                        Back
                    </button>
                )}

                {onSaveAsDraft && (
                    <Button
                        size="medium"
                        label="Save as Draft"
                        className={styles.continueButton}
                        disabled={saveAsDraftDisabled}
                        onClick={onSaveAsDraft}
                        variant="black"
                    />
                )}

                {!hideContinueButton && (
                    <Button
                        size="medium"
                        label={continueLabel}
                        className={styles.continueButton}
                        disabled={continueDisabled}
                        onClick={onContinue}
                        variant="green"
                    />
                )}

                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                >
                    ×
                </button>
            </div>
        </div>
    );
};
